/**
 * chainRoot — the one place that knows how a CHAIN is named in a URL, read by both sides of
 * `GET /chain/:root` (the client builds the path from what it armed, `ui/chains.ts`'s
 * `armedChainRoot`; the route parses it back, `products/<house>/routes/chain.$root.ts`).
 *
 * The name is the chain key the reader already groups on — `COALESCE("rootTileId"::text,
 * "rootGenerationId")` (`mine.server.ts`'s DISTINCT ON) — so the two id spaces of a root live in
 * one path segment: all digits = a chain rooted on a wall tile (`rootTileId`), anything else = a
 * from-scratch chain rooted on its own first row (`rootGenerationId`). The branch is safe because a
 * generation id is never all digits — the rows on dev are hyphenated UUIDs
 * (`f015d803-5f00-41c4-9cd1-350806af267c`), a cuid begins with a letter.
 *
 * Pure and dependency-free, so the route's own parsing is spec'd without a DB
 * (`__tests__/chainRoot.spec.ts`).
 */

/** A chain's root, as `Armed`'s two non-ghost members already spell it (`ui/chains.ts`) — the
 *  ghost has no root of its own and therefore no history to ask for. */
export type ChainRoot = {kind: 'tile'; tileId: number} | {kind: 'scratch'; rootId: string}

/** Bounds the path segment a hostile URL can put into a parameterised query — a generation id is
 *  36 characters at its longest (a UUID); anything past this is not an id. */
const MAX_ROOT_ID_CHARS = 64
const GENERATION_ID = /^[A-Za-z0-9_-]+$/

export function parseChainRoot(param: string | undefined): ChainRoot | null {
    if (!param) return null
    if (/^\d+$/.test(param)) {
        const tileId = Number(param)
        return Number.isSafeInteger(tileId) && tileId >= 0 ? {kind: 'tile', tileId} : null
    }
    if (param.length > MAX_ROOT_ID_CHARS || !GENERATION_ID.test(param)) return null
    return {kind: 'scratch', rootId: param}
}

/** The inverse — every character of both branches is already URL-safe, so no escaping is needed
 *  (and none is applied: a value this function would have to escape is one `parseChainRoot` would
 *  reject on the way back). */
export function chainRootPath(root: ChainRoot): string {
    return root.kind === 'tile' ? String(root.tileId) : root.rootId
}
